/**
 * Technische Anlagen + Zähler + Energieausweis — property-level section on the
 * Impower property page, data from one Lux `GET /api/technical-assets?propertyId=…&include=display`
 * call (assets with documents, per-asset type-field highlights and resolved contact names).
 * Lux create/detail URLs are always available (independent of Impower view/edit mode).
 * Render helpers live in `technicalAssetsSection.render.js`,
 * `technicalAssetsSection.energyCertificates.js` and `technicalAssetsSection.meters.js`.
 */
const TECHNICAL_ASSET_ENERGY_CERTIFICATE_KIND = 'Energieausweis'
const TECHNICAL_ASSET_METER_KIND = 'Zähler'
const TECHNICAL_ASSET_SEPARATE_TABLE_KINDS = new Set([
  TECHNICAL_ASSET_ENERGY_CERTIFICATE_KIND,
  TECHNICAL_ASSET_METER_KIND,
])

/**
 * One entry per sub-section; array order = DOM order inside the section.
 * Every sub-section renders from the same fetch.
 */
function technicalAssetSubSectionDefs() {
  return [
    {
      key: 'assets',
      title: 'Technische Anlagen',
      idPrefix: 'technicalAnlagenLux',
      createTyp: undefined,
      deleteConfirmText:
        'Möchten Sie diese technische Anlage wirklich löschen?',
      loadingText: 'Technische Anlagen werden geladen…',
      searchPlaceholder: 'Anlage, Typ, Gebäude, Firma suchen…',
      filter: (a) => !TECHNICAL_ASSET_SEPARATE_TABLE_KINDS.has(a.typ),
      renderTable: renderTechnicalAssetsTableHtml,
    },
    {
      key: 'meters',
      title: 'Zähler',
      idPrefix: 'zaehlerLux',
      createTyp: TECHNICAL_ASSET_METER_KIND,
      deleteConfirmText: 'Möchten Sie diesen Zähler wirklich löschen?',
      loadingText: 'Zähler werden geladen…',
      searchPlaceholder: 'Zähler, Nummer, Gebäude, Netzbetreiber suchen…',
      filter: (a) => a.typ === TECHNICAL_ASSET_METER_KIND,
      renderTable: renderMetersTableHtml,
    },
    {
      key: 'energyCertificate',
      title: 'Energieausweis',
      idPrefix: 'energieausweisLux',
      createTyp: TECHNICAL_ASSET_ENERGY_CERTIFICATE_KIND,
      deleteConfirmText: 'Möchten Sie diesen Energieausweis wirklich löschen?',
      loadingText: 'Energieausweise werden geladen…',
      searchPlaceholder: 'Energieausweis, Gebäude, Firma suchen…',
      filter: (a) => a.typ === TECHNICAL_ASSET_ENERGY_CERTIFICATE_KIND,
      renderTable: renderEnergyCertificatesTableHtml,
    },
  ]
}

/** @param {Array<{ id?: number|string, name?: string, buildingHrId?: string }>} buildings Impower buildings of the property */
function technicalAssetBuildingById(buildings, buildingId) {
  return (
    (buildings || []).find((x) => x && String(x.id) === String(buildingId)) ||
    null
  )
}

/** Name, else the Gebäude-ID (`buildingHrId`), else „Gebäude <internal id>“. */
function technicalAssetBuildingLabel(building, buildingId) {
  return (
    (building && (building.name || building.buildingHrId)) ||
    `Gebäude ${buildingId}`
  )
}

/** All assets of the property matching `def`, sorted by building label, then name. */
function assetsForTechnicalAssetSubSection(byBuildingId, buildings, def) {
  return Object.values(byBuildingId || {})
    .flat()
    .filter(def.filter)
    .map((a) => {
      const building = technicalAssetBuildingById(buildings, a.buildingId)
      return {
        ...a,
        buildingLabel: technicalAssetBuildingLabel(building, a.buildingId),
        buildingHrId: (building && building.buildingHrId) || null,
      }
    })
    .sort(
      (x, y) =>
        x.buildingLabel.localeCompare(y.buildingLabel, 'de') ||
        String(x.name || '').localeCompare(String(y.name || ''), 'de')
    )
}

/**
 * Lux `withAuth` routes: session cookie, no Impower Bearer. `path` is relative to
 * `baseApiUrl`, which differs from the Impower host on app.local (localhost:3000).
 */
async function luxTechnicalAssetsRequest(path, init) {
  const res = await fetch(`${baseApiUrl}${path}`, {
    ...init,
    cache: 'no-store',
    credentials: 'include',
    headers: { Accept: 'application/json', ...(init?.headers || {}) },
  })
  const text = await res.text()
  let data = null
  try {
    data = text ? JSON.parse(text) : null
  } catch (_) {
    data = null
  }
  if (!res.ok) {
    const msg = (data && data.error) || text || res.statusText || 'API-Fehler'
    throw new Error(msg)
  }
  return data
}

/**
 * @returns {Promise<{ byBuildingId: Record<string, Array<object>>, highlightsByAssetId: Record<string, Array<object>>, contactNames: Record<string, string> }>}
 */
async function fetchTechnicalAssetsByPropertyId(propertyId) {
  const url = `/api/technical-assets?propertyId=${encodeURIComponent(String(propertyId))}&include=display`
  const data = (await luxTechnicalAssetsRequest(url)) || {}
  return {
    byBuildingId: data.byBuildingId || {},
    highlightsByAssetId: data.highlightsByAssetId || {},
    contactNames: data.contactNames || {},
  }
}

function deleteTechnicalAssetById(assetId) {
  const url = `/api/technical-assets/${encodeURIComponent(String(assetId))}`
  return luxTechnicalAssetsRequest(url, { method: 'DELETE' })
}

/**
 * Search/filter controls above the table, built once per host. Typing or
 * choosing a filter re-renders only the table card, so the input keeps focus.
 */
function ensureTechnicalAssetControls(tableHost, ctx) {
  let controls = tableHost.querySelector(':scope > .impowerx-ta-controls')
  if (controls) return controls
  controls = buildTechnicalAssetControls({
    showKind: ctx.def.key === 'assets',
    searchPlaceholder: ctx.def.searchPlaceholder || 'Suchen…',
  })
  const view = ctx.view
  controls.addEventListener('input', (event) => {
    const input = event.target.closest('[data-ta-search]')
    if (!input) return
    view.search = input.value
    view.page = 1
    rerenderTechnicalAssetSubSectionTable(tableHost)
  })
  controls.addEventListener('change', (event) => {
    const select = event.target.closest('[data-ta-filter]')
    if (!select) return
    view[select.getAttribute('data-ta-filter')] = select.value
    view.page = 1
    rerenderTechnicalAssetSubSectionTable(tableHost)
  })
  tableHost.prepend(controls)
  return controls
}

/**
 * @param {HTMLElement} tableHost
 * @param {{ propertyId: number, propertyHrId: string, buildings: Array<object>, def: object, setCount: Function, view?: object }} ctx
 * @param {{ byBuildingId: object, highlightsByAssetId: object, contactNames: object }} data
 */
function renderTechnicalAssetSubSectionTable(tableHost, ctx, data) {
  if (!ctx.view) ctx.view = emptyTechnicalAssetView()
  tableHost._impowerxTaData = data
  const assets = assetsForTechnicalAssetSubSection(
    data.byBuildingId,
    ctx.buildings,
    ctx.def
  )
  const renderCtx = {
    buildings: ctx.buildings,
    contactNames: data.contactNames,
    highlightsByAssetId: data.highlightsByAssetId,
    detailHref: (a) => luxTechnicalAssetDetailUrl(ctx.propertyHrId, a.id),
    view: ctx.view,
    allRows: assets,
    total: assets.length,
  }
  // Loading/error copy replaced the host's children; rebuild the two slots.
  let card = tableHost.querySelector(':scope > .impowerx-ta-card')
  if (!card) {
    tableHost.replaceChildren()
    card = document.createElement('div')
    card.className = 'impowerx-ta-card'
    tableHost.appendChild(card)
  }
  const controls = ensureTechnicalAssetControls(tableHost, ctx)
  const buildingIds = new Set(assets.map((a) => String(a.buildingId)))
  refreshTechnicalAssetControls(
    controls,
    {
      buildings: [...buildingIds]
        .map((id) => ({
          value: id,
          label: technicalAssetBuildingLabel(
            technicalAssetBuildingById(ctx.buildings, id),
            id
          ),
        }))
        .sort((x, y) => x.label.localeCompare(y.label, 'de')),
      kinds: technicalAssetKindOptions(assets),
    },
    ctx.view
  )
  // A cleared filter (its option vanished) widens the rows again.
  const rows = filterTechnicalAssetRows(assets, ctx.view, renderCtx)
  card.innerHTML = ctx.def.renderTable(rows, renderCtx)
  ctx.setCount(assets.length)
  wireTechnicalAssetsRowActions(tableHost, ctx)
}

/**
 * Loading / error / notice state of a sub-section, shown like the insurance
 * table: the card with its controls and the table header stays, the body holds
 * a spinner row (`loading`), a red message (`error`) or a grey one (`notice`).
 *
 * @param {{ loading?: boolean, error?: string, notice?: string }} state
 */
function renderTechnicalAssetSubSectionState(tableHost, ctx, state) {
  if (!ctx.view) ctx.view = emptyTechnicalAssetView()
  let card = tableHost.querySelector(':scope > .impowerx-ta-card')
  if (!card) {
    tableHost.replaceChildren()
    card = document.createElement('div')
    card.className = 'impowerx-ta-card'
    tableHost.appendChild(card)
  }
  ensureTechnicalAssetControls(tableHost, ctx)
  card.innerHTML = ctx.def.renderTable([], {
    buildings: ctx.buildings,
    contactNames: {},
    highlightsByAssetId: {},
    detailHref: (a) => luxTechnicalAssetDetailUrl(ctx.propertyHrId, a.id),
    view: ctx.view,
    allRows: [],
    total: 0,
    loading: state.loading ? ctx.def.loadingText || 'Wird geladen…' : undefined,
    error: state.error,
    notice: state.notice,
  })
}

/** Re-render from the data of the last fetch (sort, page, search or filter changed). */
function rerenderTechnicalAssetSubSectionTable(tableHost) {
  const ctx = tableHost._impowerxTaCtx
  const data = tableHost._impowerxTaData
  if (!ctx || !data) return
  renderTechnicalAssetSubSectionTable(tableHost, ctx, data)
}

/**
 * Set when the user follows a create/detail link. A property tab that stays open
 * (new tab, or the list already mounted) reads this on focus and refetches.
 */
const TECHNICAL_ASSETS_LIST_DIRTY_KEY = 'lux-ta-list-dirty'
const TECHNICAL_ASSETS_REFRESHED_EVENT = 'lux-technical-assets-refreshed'

function notifyTechnicalAssetsRefreshed(propertyId) {
  document.dispatchEvent(
    new CustomEvent(TECHNICAL_ASSETS_REFRESHED_EVENT, {
      detail: { propertyId },
    })
  )
}

function markTechnicalAssetsListDirty() {
  try {
    sessionStorage.setItem(TECHNICAL_ASSETS_LIST_DIRTY_KEY, '1')
  } catch (_) {
    /* private mode / blocked storage: the pageshow refresh still covers same-tab back */
  }
}

function technicalAssetsListIsDirty() {
  try {
    return sessionStorage.getItem(TECHNICAL_ASSETS_LIST_DIRTY_KEY) === '1'
  } catch (_) {
    return false
  }
}

function clearTechnicalAssetsListDirty() {
  try {
    sessionStorage.removeItem(TECHNICAL_ASSETS_LIST_DIRTY_KEY)
  } catch (_) {
    // sessionStorage unavailable (privacy mode): nothing to clear.
  }
}

/**
 * Re-fetch (once per property) and re-render all mounted sub-section tables.
 * Each table host carries its render context in `_impowerxTaCtx`.
 * Skipped while a delete confirmation or an in-row action is open, so a return
 * to the tab does not tear down that UI.
 */
function refreshAllInjectedTechnicalAssetTables() {
  if (
    document.querySelector('.lux-confirm-pop') ||
    document.querySelector('.impowerx-ta-row-busy')
  ) {
    return
  }
  const targetsByProperty = new Map()
  document
    .querySelectorAll(
      '.impowerx-technical-assets-lux-root .impowerx-technical-assets-lux-wrap'
    )
    .forEach((tableHost) => {
      const ctx = tableHost._impowerxTaCtx
      if (!ctx || !ctx.propertyId) return
      const list = targetsByProperty.get(ctx.propertyId) || []
      list.push({ tableHost, ctx })
      targetsByProperty.set(ctx.propertyId, list)
    })
  targetsByProperty.forEach((targets, propertyId) => {
    fetchTechnicalAssetsByPropertyId(propertyId)
      .then((data) => {
        clearTechnicalAssetsListDirty()
        targets.forEach(({ tableHost, ctx }) => {
          if (!tableHost.isConnected) return
          renderTechnicalAssetSubSectionTable(tableHost, ctx, data)
        })
        notifyTechnicalAssetsRefreshed(propertyId)
      })
      .catch(() => {
        // Leave existing tables; the next return to the tab tries again
      })
  })
}

/** Coalesce pageshow + visibility + focus into one refetch. */
function scheduleTechnicalAssetsTableRefresh() {
  if (window.__impowerxTaRefreshTimer) return
  window.__impowerxTaRefreshTimer = window.setTimeout(() => {
    window.__impowerxTaRefreshTimer = 0
    refreshAllInjectedTechnicalAssetTables()
  }, 0)
}

/**
 * Keep an already-rendered list current without a full page reload:
 * - pageshow: back/forward, including bfcache restores (persisted or not — the
 *   listener is attached only after the section content exists, so the initial
 *   load's pageshow does not double-fetch)
 * - visibilitychange: the property tab was in the background while an asset was
 *   saved elsewhere
 * - focus: same, when the dirty flag was set by a create/detail click
 */
function ensureTechnicalAssetsTableRefreshOnBackNavigation() {
  if (window.__impowerxTaPageshowWired) return
  window.__impowerxTaPageshowWired = true
  window.addEventListener('pageshow', () => {
    scheduleTechnicalAssetsTableRefresh()
  })
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return
    scheduleTechnicalAssetsTableRefresh()
  })
  window.addEventListener('focus', () => {
    if (!technicalAssetsListIsDirty()) return
    scheduleTechnicalAssetsTableRefresh()
  })
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.(
      '.impowerx-technical-assets-lux-root a[href]'
    )
    if (!link) return
    if (
      !String(link.getAttribute('href') || '').includes('/technical-assets/')
    ) {
      return
    }
    markTechnicalAssetsListDirty()
  })
}

/**
 * Delete via the shared `confirmPopover`; the edit icon is a plain link to the Lux detail page.
 * The Sachbearbeiter "+N" button reveals the rest of its avatar stack.
 */
function wireTechnicalAssetsRowActions(tableHost, ctx) {
  if (tableHost._impowerxTaRowClick) {
    tableHost.removeEventListener('click', tableHost._impowerxTaRowClick)
  }
  if (tableHost._impowerxTaKeydown) {
    tableHost.removeEventListener('keydown', tableHost._impowerxTaKeydown)
  }

  const sortBy = (header) => {
    ctx.view.sort = nextTechnicalAssetSort(
      ctx.view.sort,
      header.getAttribute('data-sort-key')
    )
    ctx.view.page = 1
    rerenderTechnicalAssetSubSectionTable(tableHost)
  }

  const onKeydown = (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return
    const header = e.target.closest('th[data-sort-key]')
    if (!header) return
    e.preventDefault()
    sortBy(header)
  }

  const onClick = async (e) => {
    const header = e.target.closest('th[data-sort-key]')
    if (header) {
      e.preventDefault()
      sortBy(header)
      return
    }
    const pageButton = e.target.closest('button[data-ta-page]')
    if (pageButton) {
      e.preventDefault()
      if (pageButton.disabled) return
      const page = Number.parseInt(
        pageButton.getAttribute('data-ta-page') || '',
        10
      )
      if (!Number.isFinite(page)) return
      ctx.view.page = page
      rerenderTechnicalAssetSubSectionTable(tableHost)
      return
    }
    const more = e.target.closest('.impowerx-ta-avatar-more')
    if (more) {
      e.preventDefault()
      const stack = more.closest('.impowerx-ta-avatar-stack')
      if (stack) stack.classList.add('is-expanded')
      more.remove()
      return
    }
    const del = e.target.closest('.impowerx-ta-delete-btn')
    if (!del || del.disabled) return
    e.preventDefault()
    const row = del.closest('.impowerx-ta-row')
    const assetId = Number.parseInt(
      (row && row.getAttribute('data-row-key')) || '',
      10
    )
    if (!Number.isFinite(assetId)) return
    const name = row.getAttribute('data-row-name') || ''
    const confirmed = await confirmPopover(
      del,
      name ? { name } : { message: ctx.def.deleteConfirmText }
    )
    if (!confirmed) return
    setTechnicalAssetRowBusy(row, del)
    try {
      await deleteTechnicalAssetById(assetId)
    } catch (err) {
      showToast((err && err.message) || 'Löschen fehlgeschlagen.', 'error')
    }
    try {
      renderTechnicalAssetSubSectionTable(
        tableHost,
        ctx,
        await fetchTechnicalAssetsByPropertyId(ctx.propertyId)
      )
      notifyTechnicalAssetsRefreshed(ctx.propertyId)
    } catch (err) {
      // The old rows stay on screen; release this one so the next tab-focus
      // refresh (and the row's own buttons) work again.
      if (row.isConnected) clearTechnicalAssetRowBusy(row, del, 'delete')
      showToast(
        (err && err.message) ||
          'Technische Anlagen konnten nicht geladen werden.',
        'error'
      )
    }
  }
  tableHost._impowerxTaRowClick = onClick
  tableHost.addEventListener('click', onClick)
  tableHost._impowerxTaKeydown = onKeydown
  tableHost.addEventListener('keydown', onKeydown)
}

/**
 * Content of the property-level "Technische Anlagen" section: one sub-section per def
 * (Technische Anlagen, Zähler, Energieausweis), listing the assets of all buildings.
 *
 * @param {object} opts
 * @param {number|undefined|null} opts.propertyId Lux/Impower property id
 * @param {string} opts.propertyHrId
 * @param {Array<{ id?: number|string, name?: string }>} opts.buildings
 * @param {Promise<Array<{ id?: number|string, name?: string }>>} [opts.loadedBuildings] Full building list; replaces `buildings` once it resolves
 * @param {Promise<object>} [opts.assetsRequest] List request already started with the page
 *   (sections are built lazily on open); the data is usually there by then
 * @returns {HTMLElement}
 */
function createTechnicalAssetsSectionContent({
  propertyId,
  propertyHrId,
  buildings,
  loadedBuildings,
  assetsRequest,
}) {
  ensureTechnicalAssetsTableStyles()
  ensureTechnicalAssetsTableRefreshOnBackNavigation()

  const container = document.createElement('div')
  const mounted = technicalAssetSubSectionDefs().map((def) => {
    const { wrap, tableHost, setCount } = buildTechnicalAssetSubSection({
      def,
      propertyHrId,
      propertyId,
    })
    container.appendChild(wrap)
    const ctx = { propertyId, propertyHrId, buildings, def, setCount }
    tableHost._impowerxTaCtx = ctx
    return { tableHost, ctx }
  })

  const showState = (state) => {
    mounted.forEach(({ tableHost, ctx }) =>
      renderTechnicalAssetSubSectionState(tableHost, ctx, state)
    )
  }

  if (!propertyId) {
    showState({
      notice: 'Lux-Objekt-ID der Liegenschaft fehlt — Liste nicht verfügbar.',
    })
    return container
  }

  let assetData = null
  const renderTables = () => {
    if (!assetData) return
    mounted.forEach(({ tableHost, ctx }) =>
      renderTechnicalAssetSubSectionTable(tableHost, ctx, assetData)
    )
  }
  if (loadedBuildings) {
    loadedBuildings
      .then((list) => {
        if (!Array.isArray(list) || !list.length) return
        mounted.forEach(({ ctx }) => {
          ctx.buildings = list
        })
        renderTables()
      })
      .catch(() => {})
  }

  showState({ loading: true })
  // Later changes (delete here, saves on other pages) refetch via the row
  // actions and the pageshow/visibility/focus refresh.
  const request = assetsRequest || fetchTechnicalAssetsByPropertyId(propertyId)
  request
    .then((data) => {
      clearTechnicalAssetsListDirty()
      assetData = data
      renderTables()
    })
    .catch((e) => {
      showState({
        error:
          (e && e.message) ||
          'Technische Anlagen konnten nicht geladen werden.',
      })
    })

  return container
}
